import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormControlLabel,
  Switch,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  InputAdornment,
  SelectChangeEvent
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Build as BuildIcon,
  DirectionsBoat as BoatIcon,
  LocalShipping as TrailerIcon,
  Download as DownloadIcon,
  Assignment as AssignmentIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { Trailer, Vessel } from '../../models/types';
import { 
  getTrailers, 
  createTrailer, 
  updateTrailer, 
  deleteTrailer, 
  assignTrailerToVessel, 
  unassignTrailerFromVessel,
  updateTrailerMaintenance
} from '../../firebase/services/trailerService';
import { getVessels } from '../../firebase/services/vesselService';
import { useNavigate } from 'react-router-dom';
import TrailerRegistrationDialog from '../../components/TrailerRegistrationDialog';

const TrailerManagement: React.FC = () => {
  const navigate = useNavigate();
  const [trailers, setTrailers] = useState<Trailer[]>([]);
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Dialog states
  const [trailerDialogOpen, setTrailerDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [registrationDialogOpen, setRegistrationDialogOpen] = useState(false);
  
  // Form states
  const [trailerForm, setTrailerForm] = useState<Partial<Trailer>>({
    vin: '',
    licensePlate: '',
    manufacturer: '',
    model: '',
    year: new Date().getFullYear(),
    capacity: 0,
    placedInServiceDate: new Date(),
    isActive: true,
    notes: ''
  });
  
  const [selectedTrailer, setSelectedTrailer] = useState<Trailer | null>(null);
  const [selectedVesselId, setSelectedVesselId] = useState('');
  const [maintenanceForm, setMaintenanceForm] = useState({
    lastMaintenanceDate: new Date(),
    nextMaintenanceDate: new Date(new Date().setMonth(new Date().getMonth() + 3)),
    maintenanceType: ''
  });
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const [filterAssignment, setFilterAssignment] = useState('all');
  
  // Load trailers and vessels
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [fetchedTrailers, fetchedVessels] = await Promise.all([
          getTrailers(),
          getVessels()
        ]);
        
        setTrailers(fetchedTrailers);
        setVessels(fetchedVessels);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Handle form changes
  const handleTrailerFormChange = (field: keyof Trailer) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setTrailerForm(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };
  
  const handleNumberChange = (field: keyof Trailer) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setTrailerForm(prev => ({
      ...prev,
      [field]: Number(event.target.value)
    }));
  };
  
  const handleDateChange = (field: keyof Trailer) => (date: Date | null) => {
    if (date) {
      setTrailerForm(prev => ({
        ...prev,
        [field]: date
      }));
    }
  };
  
  const handleMaintenanceDateChange = (field: 'lastMaintenanceDate' | 'nextMaintenanceDate') => (date: Date | null) => {
    if (date) {
      setMaintenanceForm(prev => ({
        ...prev,
        [field]: date
      }));
    }
  };
  
  const handleVesselChange = (event: SelectChangeEvent) => {
    setSelectedVesselId(event.target.value);
  };
  
  const handleStatusChange = (event: SelectChangeEvent) => {
    setFilterStatus(event.target.value);
  };
  
  const handleAssignmentChange = (event: SelectChangeEvent) => {
    setFilterAssignment(event.target.value);
  };
  
  // Open dialogs
  const handleAddTrailerClick = () => {
    setSelectedTrailer(null);
    setTrailerForm({
      vin: '',
      licensePlate: '',
      manufacturer: '',
      model: '',
      year: new Date().getFullYear(),
      capacity: 0,
      placedInServiceDate: new Date(),
      isActive: true,
      notes: ''
    });
    setTrailerDialogOpen(true);
  };
  
  const handleEditTrailerClick = (trailer: Trailer) => {
    setSelectedTrailer(trailer);
    setTrailerForm({
      vin: trailer.vin,
      licensePlate: trailer.licensePlate,
      manufacturer: trailer.manufacturer,
      model: trailer.model,
      year: trailer.year,
      capacity: trailer.capacity,
      placedInServiceDate: trailer.placedInServiceDate,
      isActive: trailer.isActive,
      notes: trailer.notes || ''
    });
    setTrailerDialogOpen(true);
  };
  
  const handleDeleteClick = (trailer: Trailer) => {
    setSelectedTrailer(trailer);
    setDeleteDialogOpen(true);
  };
  
  const handleMaintenanceClick = (trailer: Trailer) => {
    setSelectedTrailer(trailer);
    setMaintenanceForm({
      lastMaintenanceDate: trailer.lastMaintenanceDate || new Date(),
      nextMaintenanceDate: trailer.nextMaintenanceDate || new Date(new Date().setMonth(new Date().getMonth() + 3)),
      maintenanceType: trailer.maintenanceType || ''
    });
    setMaintenanceDialogOpen(true);
  };
  
  const handleAssignClick = (trailer: Trailer) => {
    setSelectedTrailer(trailer);
    setSelectedVesselId(trailer.assignedVesselId || '');
    setAssignDialogOpen(true);
  };
  
  // Close dialogs
  const handleCloseTrailerDialog = () => {
    setTrailerDialogOpen(false);
    setSelectedTrailer(null);
  };
  
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedTrailer(null);
  };
  
  const handleCloseMaintenanceDialog = () => {
    setMaintenanceDialogOpen(false);
    setSelectedTrailer(null);
  };
  
  const handleCloseAssignDialog = () => {
    setAssignDialogOpen(false);
    setSelectedTrailer(null);
  };
  
  // Save handlers
  const handleSaveTrailer = async () => {
    try {
      if (!trailerForm.vin || !trailerForm.licensePlate || !trailerForm.manufacturer || !trailerForm.model) {
        setError('Please fill in all required fields');
        return;
      }
      
      if (selectedTrailer) {
        // Update existing trailer
        await updateTrailer(selectedTrailer.id, trailerForm);
        setSuccessMessage(`Trailer ${trailerForm.licensePlate} updated successfully`);
      } else {
        // Create new trailer
        await createTrailer(trailerForm as Omit<Trailer, 'id' | 'createdAt' | 'updatedAt'>);
        setSuccessMessage(`Trailer ${trailerForm.licensePlate} added successfully`);
      }
      
      // Refresh trailers
      const updatedTrailers = await getTrailers();
      setTrailers(updatedTrailers);
      
      handleCloseTrailerDialog();
    } catch (err) {
      console.error('Error saving trailer:', err);
      setError('Failed to save trailer. Please try again.');
    }
  };
  
  const handleConfirmDelete = async () => {
    if (!selectedTrailer) return;
    
    try {
      await deleteTrailer(selectedTrailer.id);
      setSuccessMessage(`Trailer ${selectedTrailer.licensePlate} deleted successfully`);
      
      // Refresh trailers
      const updatedTrailers = await getTrailers();
      setTrailers(updatedTrailers);
      
      handleCloseDeleteDialog();
    } catch (err) {
      console.error('Error deleting trailer:', err);
      setError('Failed to delete trailer. Please try again.');
    }
  };
  
  const handleSaveMaintenance = async () => {
    if (!selectedTrailer) return;
    
    try {
      await updateTrailerMaintenance(
        selectedTrailer.id,
        maintenanceForm.lastMaintenanceDate,
        maintenanceForm.nextMaintenanceDate,
        maintenanceForm.maintenanceType
      );
      
      setSuccessMessage(`Maintenance record for ${selectedTrailer.licensePlate} updated successfully`);
      
      // Refresh trailers
      const updatedTrailers = await getTrailers();
      setTrailers(updatedTrailers);
      
      handleCloseMaintenanceDialog();
    } catch (err) {
      console.error('Error updating maintenance:', err);
      setError('Failed to update maintenance record. Please try again.');
    }
  };
  
  const handleSaveAssignment = async () => {
    if (!selectedTrailer) return;
    
    try {
      if (selectedVesselId) {
        const selectedVessel = vessels.find(v => v.id === selectedVesselId);
        if (selectedVessel) {
          await assignTrailerToVessel(selectedTrailer.id, selectedVessel.id, selectedVessel.name);
          setSuccessMessage(`Trailer ${selectedTrailer.licensePlate} assigned to ${selectedVessel.name}`);
        }
      } else {
        await unassignTrailerFromVessel(selectedTrailer.id);
        setSuccessMessage(`Trailer ${selectedTrailer.licensePlate} unassigned from vessel`);
      }
      
      // Refresh trailers
      const updatedTrailers = await getTrailers();
      setTrailers(updatedTrailers);
      
      handleCloseAssignDialog();
    } catch (err) {
      console.error('Error assigning trailer:', err);
      setError('Failed to assign trailer. Please try again.');
    }
  };
  
  // Filter trailers
  const filteredTrailers = trailers.filter(trailer => {
    const matchesSearch = 
      trailer.licensePlate.toLowerCase().includes(searchTerm.toLowerCase()) ||
      trailer.vin.toLowerCase().includes(searchTerm.toLowerCase()) ||
      trailer.manufacturer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      trailer.model.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = 
      filterStatus === 'all' ? true :
      filterStatus === 'active' ? trailer.isActive :
      filterStatus === 'inactive' ? !trailer.isActive :
      filterStatus === 'deleted' ? trailer.status === 'deleted' : true;
    
    return matchesSearch && matchesStatus;
  });
  
  return (
    <Box sx={{ p: { xs: 2, sm: 2, md: 2 } }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Trailer Management
      </Typography>
      
      {/* Success and error messages */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%', 
            background: 'linear-gradient(135deg, #E3F2FD 0%, #FFFFFF 100%)',
            boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)'
          }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" color="primary.main">Total Trailers</Typography>
                <Box sx={{ 
                  backgroundColor: 'primary.light',
                  borderRadius: '50%',
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40
                }}>
                  <TrailerIcon color="primary" />
                </Box>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 600 }}>{trailers.length}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%', 
            background: 'linear-gradient(135deg, #E8F5E9 0%, #FFFFFF 100%)',
            boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)'
          }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" color="success.main">Active</Typography>
                <Box sx={{ 
                  backgroundColor: 'success.light',
                  borderRadius: '50%',
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40
                }}>
                  <TrailerIcon sx={{ color: 'success.main' }} />
                </Box>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 600 }}>
                {trailers.filter(t => t.isActive).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%', 
            background: 'linear-gradient(135deg, #FFF3E0 0%, #FFFFFF 100%)',
            boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)'
          }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" color="warning.main">Reg Due</Typography>
                <Box sx={{ 
                  backgroundColor: 'warning.light',
                  borderRadius: '50%',
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40
                }}>
                  <ReceiptIcon sx={{ color: 'warning.main' }} />
                </Box>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 600 }}>
                {trailers.filter(t => {
                  if (!t.stateRegistrationExpiry) return false;
                  const expiryDate = new Date(t.stateRegistrationExpiry);
                  const today = new Date();
                  const thirtyDaysFromNow = new Date();
                  thirtyDaysFromNow.setDate(today.getDate() + 30);
                  return expiryDate >= today && expiryDate <= thirtyDaysFromNow;
                }).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%', 
            background: 'linear-gradient(135deg, #FFEBEE 0%, #FFFFFF 100%)',
            boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)'
          }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" color="error.main">Maintenance Due</Typography>
                <Box sx={{ 
                  backgroundColor: 'error.light',
                  borderRadius: '50%',
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40
                }}>
                  <BuildIcon sx={{ color: 'error.main' }} />
                </Box>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 600 }}>
                {trailers.filter(t => t.nextMaintenanceDate && new Date(t.nextMaintenanceDate) <= new Date()).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action buttons */}
      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        mb: 3, 
        flexWrap: 'wrap',
        '& .MuiButton-root': {
          minWidth: 140,
          height: 40,
          fontWeight: 500,
          textTransform: 'none',
          borderRadius: 1,
          boxShadow: 'none'
        }
      }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddTrailerClick}
        >
          Add New Trailer
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<BuildIcon />}
          onClick={() => setMaintenanceDialogOpen(true)}
        >
          Maintenance Schedule
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<DownloadIcon />}
          onClick={() => {/* TODO: Implement export functionality */}}
        >
          Export Data
        </Button>
      </Box>

      {/* Filter controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search trailers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="deleted">Deleted</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Assignment</InputLabel>
              <Select
                value={filterAssignment}
                label="Assignment"
                onChange={handleAssignmentChange}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="assigned">Assigned</MenuItem>
                <MenuItem value="unassigned">Unassigned</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Trailers table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>License Plate</TableCell>
                <TableCell>VIN</TableCell>
                <TableCell>Manufacturer</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>Year</TableCell>
                <TableCell>Capacity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Registration</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTrailers.length > 0 ? (
                filteredTrailers.map(trailer => (
                  <TableRow key={trailer.id}>
                    <TableCell>{trailer.licensePlate}</TableCell>
                    <TableCell>{trailer.vin}</TableCell>
                    <TableCell>{trailer.manufacturer}</TableCell>
                    <TableCell>{trailer.model}</TableCell>
                    <TableCell>{trailer.year}</TableCell>
                    <TableCell>{trailer.capacity} lbs</TableCell>
                    <TableCell>
                      <Chip 
                        label={trailer.isActive ? 'Active' : 'Inactive'} 
                        size="small" 
                        color={trailer.isActive ? 'success' : 'error'} 
                      />
                    </TableCell>
                    <TableCell>
                      {trailer.stateRegistrationNumber ? (
                        <Box>
                          <Typography variant="body2">
                            {trailer.stateRegistrationNumber}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {trailer.stateRegistrationState} - Expires: {format(new Date(trailer.stateRegistrationExpiry || ''), 'MM/dd/yyyy')}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Not registered
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditTrailerClick(trailer)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => handleAssignClick(trailer)}
                        >
                          <BoatIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => handleMaintenanceClick(trailer)}
                        >
                          <BuildIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => {
                            setSelectedTrailer(trailer);
                            setRegistrationDialogOpen(true);
                          }}
                        >
                          <AssignmentIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(trailer)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Box sx={{ py: 4 }}>
                      <TrailerIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        No Trailers Found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {trailers.length === 0 ? "You haven't added any trailers yet." : "No trailers match your search criteria."}
                      </Typography>
                      {trailers.length === 0 && (
                        <Button 
                          variant="contained" 
                          onClick={handleAddTrailerClick}
                          startIcon={<AddIcon />}
                        >
                          Add First Trailer
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Add/Edit Trailer Dialog */}
      <Dialog 
        open={trailerDialogOpen} 
        onClose={handleCloseTrailerDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedTrailer ? 'Edit Trailer' : 'Add New Trailer'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="License Plate"
                  value={trailerForm.licensePlate}
                  onChange={handleTrailerFormChange('licensePlate')}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="VIN"
                  value={trailerForm.vin}
                  onChange={handleTrailerFormChange('vin')}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Manufacturer"
                  value={trailerForm.manufacturer}
                  onChange={handleTrailerFormChange('manufacturer')}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Model"
                  value={trailerForm.model}
                  onChange={handleTrailerFormChange('model')}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Year"
                  type="number"
                  value={trailerForm.year}
                  onChange={handleNumberChange('year')}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Capacity (lbs)"
                  type="number"
                  value={trailerForm.capacity}
                  onChange={handleNumberChange('capacity')}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Placed in Service Date"
                    value={trailerForm.placedInServiceDate}
                    onChange={handleDateChange('placedInServiceDate')}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={trailerForm.isActive}
                      onChange={(e) => setTrailerForm(prev => ({ ...prev, isActive: e.target.checked }))}
                    />
                  }
                  label="Active"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={trailerForm.notes}
                  onChange={handleTrailerFormChange('notes')}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button onClick={handleCloseTrailerDialog}>Cancel</Button>
          <Button onClick={handleSaveTrailer} variant="contained" color="primary">
            {selectedTrailer ? 'Save Changes' : 'Add Trailer'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContent>
            Are you sure you want to delete the trailer with license plate {selectedTrailer?.licensePlate}?
            This action cannot be undone.
          </DialogContent>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Maintenance Dialog */}
      <Dialog
        open={maintenanceDialogOpen}
        onClose={handleCloseMaintenanceDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Maintenance Record - {selectedTrailer?.licensePlate}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Last Maintenance Date"
                    value={maintenanceForm.lastMaintenanceDate}
                    onChange={handleMaintenanceDateChange('lastMaintenanceDate')}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Next Maintenance Date"
                    value={maintenanceForm.nextMaintenanceDate}
                    onChange={handleMaintenanceDateChange('nextMaintenanceDate')}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Maintenance Type"
                  value={maintenanceForm.maintenanceType}
                  onChange={(e) => setMaintenanceForm(prev => ({ ...prev, maintenanceType: e.target.value }))}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button onClick={handleCloseMaintenanceDialog}>Cancel</Button>
          <Button onClick={handleSaveMaintenance} variant="contained" color="primary">
            Save Maintenance
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Assign Trailer Dialog */}
      <Dialog
        open={assignDialogOpen}
        onClose={handleCloseAssignDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Assign Trailer - {selectedTrailer?.licensePlate}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Assign to Vessel</InputLabel>
              <Select
                value={selectedVesselId}
                label="Assign to Vessel"
                onChange={handleVesselChange}
              >
                <MenuItem value="">
                  <em>Unassigned</em>
                </MenuItem>
                {vessels.map(vessel => (
                  <MenuItem key={vessel.id} value={vessel.id}>
                    {vessel.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button onClick={handleCloseAssignDialog}>Cancel</Button>
          <Button onClick={handleSaveAssignment} variant="contained" color="primary">
            Save Assignment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Registration Dialog */}
      <TrailerRegistrationDialog
        open={registrationDialogOpen}
        onClose={() => {
          setRegistrationDialogOpen(false);
          setSelectedTrailer(null);
        }}
        trailer={selectedTrailer}
        onUpdate={async () => {
          const updatedTrailers = await getTrailers();
          setTrailers(updatedTrailers);
        }}
      />
    </Box>
  );
};

export default TrailerManagement; 